import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material'
import LoginIcon from '@mui/icons-material/Login'
import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { EditIcon } from 'src/v3/core/icons'
import { useState } from 'react'
import ConfirmAction from 'src/v3/components/confirm-action'
import { CopyToClipboard } from 'src/v3/components/copy-to-clipboard'
import { useAuth0 } from '@auth0/auth0-react'
import {
  selectSelectedLinkedService,
  errorMessageChanged,
  selectLinkedServiceById,
  fetchLinkedServices,
} from '../admin.slice'

export default ({
  isSaving,
  setFormOpen,
  confirmOpen,
}: {
  isSaving: (bool: boolean) => void
  setFormOpen: (form: string) => void
  confirmOpen: string
}) => {
  let dispatch = useAppDispatch()
  let { getAccessTokenSilently } = useAuth0()
  let selectedLinkedService = useAppSelector(selectSelectedLinkedService)
  let linkedService = useAppSelector(
    selectLinkedServiceById(selectedLinkedService),
  )
  let [ssoSelfServiceURL, setSsoSelfServiceURL] = useState<string>('')

  let onDisableSso = () => {
    isSaving(true)
    return axios
      .put('/api/admin/linked-service/organization-id', {
        id: selectedLinkedService,
        organizationID: null,
      })
      .then(() => {
        dispatch(fetchLinkedServices())
        isSaving(false)
      })
      .catch((err) => {
        dispatch(
          errorMessageChanged(err?.message || 'Error Removing Organization ID'),
        )
        dispatch(fetchLinkedServices())
        isSaving(false)
      })
  }

  return (
    <>
      <Box className="w-full">
        <Box className="flex items-center gap-6" sx={{ minHeight: '50px' }}>
          <FormControlLabel
            sx={{ mr: 0 }}
            label={
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
                color="textSecondary"
              >
                SSO
              </Typography>
            }
            control={
              <Switch
                id="sso"
                checked={!!linkedService?.organizationID}
                onChange={() =>
                  linkedService?.organizationID
                    ? setFormOpen(`ls-sso-${selectedLinkedService}`)
                    : setFormOpen('sso')
                }
                color={linkedService?.organizationID ? 'primary' : 'default'}
              />
            }
          />
          {linkedService?.organizationID && (
            <Button
              variant="contained"
              size="small"
              startIcon={<LoginIcon />}
              sx={{ minHeight: '40px' }}
              onClick={async () => {
                let token = await getAccessTokenSilently()
                axios
                  .post(
                    `${import.meta.env.VITE_SUBSCRIPTION_API_URL}/api/sso-ticket`,
                    {
                      organizationID: linkedService?.organizationID,
                      name: linkedService?.name,
                    },
                    {
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                    },
                  )
                  .then(pluckData)
                  .then(setSsoSelfServiceURL)
                  .catch((err) =>
                    dispatch(
                      errorMessageChanged(
                        err?.message || 'Error generating sso ticket',
                      ),
                    ),
                  )
              }}
            >
              Generate SSO Ticket
            </Button>
          )}
        </Box>
        {!!linkedService?.organizationID && (
          <Box className="mt-2 ml-12">
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontWeight: 600 }}
                >
                  Organization ID
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setFormOpen('sso')}
                  sx={{ minWidth: 'auto', p: 0.5, ml: 1 }}
                >
                  <EditIcon sx={{ fontSize: 16 }} />
                </Button>
              </Box>
              <Typography variant="body2" color="textSecondary">
                {linkedService?.organizationID}
              </Typography>
            </Box>
            {ssoSelfServiceURL && (
              <Box>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ fontWeight: 600 }}
                >
                  SSO URL:
                </Typography>
                <code className="inline-flex items-center justify-between gap-4 p-2 bg-gray-800 rounded-lg">
                  <span className="text-xs">{ssoSelfServiceURL}</span>
                  <CopyToClipboard
                    textToCopy={ssoSelfServiceURL}
                    messageLabel="SSO URL"
                  />
                </code>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <ConfirmAction
        executeAction={() => onDisableSso()}
        open={confirmOpen}
        setOpen={setFormOpen}
        openText={`ls-sso-${selectedLinkedService}`}
        title="Confirm Disable SSO"
        text={`Are you sure you want to disable SSO for ${linkedService?.name || 'Linked Service'}?`}
        onFailure={(err) =>
          dispatch(
            errorMessageChanged(
              `Error Disabling SSO for ${linkedService?.name || 'Linked Service'}: ${err}`,
            ),
          )
        }
      />
    </>
  )
}
